import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chips } from "@/components/wizard/WizardUI";
import { CurrencyInput, fmtBRL } from "@/components/CurrencyInput";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Bookmark } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

export const EQUIPMENTS = ["Geladeira", "Lava e seca", "Máquina de lavar", "Freezer", "Cervejeira", "Ar-condicionado", "Outro"];
export const WARRANTIES = ["Sem garantia", "30 dias", "60 dias", "90 dias", "6 meses", "1 ano", "Personalizado"];

const F = ({ label, children, hint }) => <div><Label className="text-base">{label}</Label>{children}{hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}</div>;
const TA = (props) => <Textarea {...props} className={`mt-1 text-base min-h-[120px] ${props.className || ""}`} />;

export const EquipmentStep = ({ eq, setEq, saved = [], selectedId }) => {
  const isKnown = EQUIPMENTS.includes(eq.name);
  const chipValue = eq.name ? (isKnown && eq.name !== "Outro" ? eq.name : "Outro") : "";
  return (
    <div className="space-y-5">
      {saved.length > 0 && (
        <F label="Equipamentos cadastrados deste cliente" hint="Toque para usar — o serviço entra no histórico do equipamento.">
          <div className="grid gap-2 mt-2">
            {saved.map((s, i) => (
              <button key={i} type="button" onClick={() => setEq({ ...eq, ...s })} data-testid={`wizard-saved-equipment-${i}`}
                className={`text-left rounded-xl border p-3 active:scale-[0.99] transition-all ${selectedId && selectedId === s.equipment_id ? "border-primary bg-primary/5" : "border-border hover:border-primary"}`}>
                <p className="font-semibold">{s._label || s.name}</p><p className="text-xs text-muted-foreground">{[s.brand, s.model, s.serial && `S/N ${s.serial}`].filter(Boolean).join(" • ")}</p>
              </button>
            ))}
          </div>
        </F>
      )}
      <F label={saved.length ? "Ou informe outro equipamento" : "Qual é o equipamento?"}>
        <div className="mt-2"><Chips options={EQUIPMENTS} value={chipValue} onChange={(o) => setEq({ ...eq, equipment_id: "", name: o === "Outro" ? "" : o, other: o === "Outro" })} testidPrefix="wizard-equipment" /></div>
      </F>
      {(eq.other || (eq.name && !isKnown)) && <F label="Informe o equipamento"><Input value={eq.name} onChange={(e) => setEq({ ...eq, name: e.target.value })} placeholder="Ex.: Micro-ondas, Adega, Fogão" className="mt-1 h-12 text-base" autoFocus data-testid="wizard-equipment-other" /></F>}
      <div className="grid grid-cols-2 gap-3">
        <F label="Marca"><Input value={eq.brand || ""} onChange={(e) => setEq({ ...eq, brand: e.target.value })} className="mt-1 h-12 text-base" data-testid="wizard-equipment-brand" /></F>
        <F label="Modelo"><Input value={eq.model || ""} onChange={(e) => setEq({ ...eq, model: e.target.value })} className="mt-1 h-12 text-base" data-testid="wizard-equipment-model" /></F>
      </div>
      <F label="Número de série (opcional)"><Input value={eq.serial || ""} onChange={(e) => setEq({ ...eq, serial: e.target.value })} className="mt-1 h-12 text-base" data-testid="wizard-equipment-serial" /></F>
    </div>
  );
};

export const TextStep = ({ label, value, onChange, placeholder, testid, hint, templates, onSaveTemplate, onDeleteTemplate }) => (
  <div className="space-y-4">
    {templates && templates.length > 0 && (
      <div data-testid="service-templates">
        <Label className="text-sm text-muted-foreground">Modelos frequentes — toque para preencher</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {templates.map((t) => (
            <span key={t} className="inline-flex items-center rounded-xl border border-border bg-card overflow-hidden">
              <button type="button" onClick={() => onChange(t)} className="h-11 px-3 text-sm text-left max-w-[240px] truncate hover:bg-accent" data-testid={`service-template-${t.slice(0, 20)}`}>{t}</button>
              {onDeleteTemplate && <button type="button" onClick={() => onDeleteTemplate(t)} aria-label="Remover modelo" className="h-11 w-9 flex items-center justify-center text-muted-foreground hover:text-destructive border-l border-border" data-testid={`service-template-delete-${t.slice(0, 20)}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </span>
          ))}
        </div>
      </div>
    )}
    <F label={label} hint={hint}><TA value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus data-testid={testid} /></F>
    {onSaveTemplate && value?.trim() && !(templates || []).includes(value.trim()) && (
      <Button type="button" variant="outline" size="sm" onClick={() => onSaveTemplate(value)} data-testid="save-service-template"><Bookmark className="h-4 w-4 mr-1" /> Salvar como modelo frequente</Button>
    )}
  </div>
);

export const ReportStep = ({ doc, upd }) => (
  <div className="space-y-4">
    <F label="Descrição técnica"><TA value={doc.description} onChange={(e) => upd({ description: e.target.value })} data-testid="wizard-report-description" className="min-h-[90px]" /></F>
    <F label="Defeito identificado"><TA value={doc.equipment?.reported_defect} onChange={(e) => upd({ equipment: { ...doc.equipment, reported_defect: e.target.value } })} data-testid="wizard-report-defect" className="min-h-[90px]" /></F>
    <F label="Diagnóstico"><TA value={doc.diagnosis} onChange={(e) => upd({ diagnosis: e.target.value })} data-testid="wizard-report-diagnosis" className="min-h-[90px]" /></F>
    <F label="Serviço recomendado"><TA value={doc.recommendation} onChange={(e) => upd({ recommendation: e.target.value })} data-testid="wizard-report-recommendation" className="min-h-[90px]" /></F>
    <F label="Observações técnicas"><TA value={doc.conclusion} onChange={(e) => upd({ conclusion: e.target.value })} data-testid="wizard-report-conclusion" className="min-h-[90px]" /></F>
  </div>
);

export const ValueStep = ({ doc, upd, label = "Valor do serviço" }) => {
  const [showItems, setShowItems] = React.useState((doc.items || []).length > 0);
  const items = doc.items || [];
  const subtotal = items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.unit_price || 0), 0);
  const setItem = (idx, k, v) => upd({ items: items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) });
  return (
    <div className="space-y-5">
      <F label={label} hint="Digite o valor normalmente: 450 ou 450,00.">
        <div className="mt-2"><CurrencyInput value={doc.labor} onChange={(v) => upd({ labor: v })} testid="wizard-value-input" autoFocus /></div>
      </F>
      {!showItems ? (
        <Button type="button" variant="outline" className="w-full h-11 border-dashed" onClick={() => { setShowItems(true); if (!items.length) upd({ items: [{ description: "", qty: 1, unit_price: 0 }] }); }} data-testid="wizard-add-items"><Plus className="h-4 w-4 mr-1" /> Adicionar peças / itens (opcional)</Button>
      ) : (
        <div className="space-y-2">
          <Label className="text-base">Peças / itens</Label>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`wizard-item-${idx}`}>
              <Input className="col-span-6 h-11" placeholder="Descrição" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} />
              <Input className="col-span-2 h-11" type="number" inputMode="decimal" placeholder="Qtd" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} />
              <Input className="col-span-3 h-11" type="number" inputMode="decimal" placeholder="Valor" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} />
              <button type="button" onClick={() => upd({ items: items.filter((_, i) => i !== idx) })} className="col-span-1 flex justify-center text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => upd({ items: [...items, { description: "", qty: 1, unit_price: 0 }] })}><Plus className="h-4 w-4 mr-1" /> Mais um item</Button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <F label="Desconto (R$)"><Input type="number" inputMode="decimal" value={doc.discount || ""} onChange={(e) => upd({ discount: e.target.value })} className="mt-1 h-12" data-testid="wizard-discount" /></F>
        <div className="rounded-xl bg-accent p-3 flex flex-col justify-center"><p className="text-xs text-muted-foreground">Total</p><p className="font-display font-extrabold text-xl text-secondary" data-testid="wizard-total">{fmtBRL(subtotal + Number(doc.labor || 0) - Number(doc.discount || 0))}</p></div>
      </div>
    </div>
  );
};

export const DateStep = ({ doc, upd, showValidity }) => (
  <div className="space-y-4">
    <F label="Data do documento" hint="Preenchida com hoje. Toque para alterar."><DatePicker value={doc.date} onChange={(v) => upd({ date: v })} testid="wizard-date" className="h-14 text-base" /></F>
    {showValidity && <F label="Validade do orçamento"><div className="mt-2"><Chips options={["7 dias", "15 dias", "30 dias"]} value={doc.validity} onChange={(v) => upd({ validity: v })} testidPrefix="wizard-validity" /></div></F>}
  </div>
);

export const WarrantyStep = ({ warranty, setWarranty }) => {
  const known = WARRANTIES.includes(warranty.term);
  const chip = warranty.term ? (known && warranty.term !== "Personalizado" ? warranty.term : "Personalizado") : "";
  return (
    <div className="space-y-5">
      <F label="Qual a garantia do serviço?"><div className="mt-2"><Chips options={WARRANTIES} value={chip} onChange={(o) => setWarranty({ ...warranty, term: o === "Personalizado" ? "" : o, custom: o === "Personalizado" })} testidPrefix="wizard-warranty" /></div></F>
      {(warranty.custom || (warranty.term && !known)) && <F label="Informe a garantia"><Input value={warranty.term} onChange={(e) => setWarranty({ ...warranty, term: e.target.value })} placeholder="Ex.: 45 dias na peça e 90 na mão de obra" className="mt-1 h-12 text-base" autoFocus data-testid="wizard-warranty-custom" /></F>}
      {warranty.term && warranty.term !== "Sem garantia" && <F label="O que está coberto (opcional)"><TA value={warranty.covered || ""} onChange={(e) => setWarranty({ ...warranty, covered: e.target.value })} className="min-h-[80px]" /></F>}
    </div>
  );
};

export const SignatureStep = ({ clientName, techName, sigs, setSigs, onTechName }) => (
  <div className="space-y-6">
    <div className="space-y-2" data-testid="wizard-sign-client">
      <Label className="text-base">Assinatura do cliente</Label>
      <p className="text-xs text-muted-foreground">{clientName}: assine com o dedo no quadro abaixo.</p>
      <div className="[&_canvas]:rounded-xl"><SignaturePad onChange={(d) => setSigs({ ...sigs, client: d })} height={180} /></div>
    </div>
    <div className="space-y-2" data-testid="wizard-sign-tech">
      <Label className="text-base">Assinatura do técnico</Label>
      <Input value={techName || ""} onChange={(e) => onTechName(e.target.value)} placeholder="Nome do técnico" className="h-11" data-testid="wizard-sign-tech-name" />
      <SignaturePad onChange={(d) => setSigs({ ...sigs, tech: d })} height={180} />
    </div>
    <p className="text-xs text-muted-foreground">As assinaturas são opcionais — você pode avançar e, na tela final, assinar depois ou <b>enviar o link para o cliente assinar pelo celular dele</b>. Ao assinar, o documento fica bloqueado para edição.</p>
  </div>
);
